import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../constants/collections';
import serverModeService from './serverModeService';

// This service intentionally reads Firebase directly. It is a cloud -> cache /
// cloud -> LAN preparation task, not a normal business-data read. Normal pages
// continue using offlineFirestore and therefore preserve their existing routing.
const CORE_COLLECTIONS = [
  'users', 'roles', 'employees', 'branches', 'growers', 'customers', 'products',
  'productVarieties', 'categories', 'agreements', 'drivers', 'vehicles',
  'financialYears', 'formSettings', 'chambers', 'bins', 'crates', 'inwards',
  'lots', 'outwards', 'warehouses', 'storageAllocations', 'storageReservations',
  'packingPackages', 'dispatches', 'loading', 'delivery', 'labelTemplates',
];

const FINANCE_COLLECTIONS = [
  'invoices', 'payments', 'financeExpenses', 'expenses', 'stockMovements',
  'crateReconciliations', 'bankReconciliations', 'cheques', 'employeeFinanceRecords',
  'accounts', 'ledger', 'transactions', 'financeAccounts', 'financeTransactions',
  'financeIncome', 'receipts', 'storageInvoices', 'gradingInvoices', 'outwardInvoices',
  'tradePayments', 'growerLedger', 'crateLedger', 'supplierLedger', 'ledgerParties',
];

const EXCLUDED_COLLECTIONS = new Set([
  'plans', 'subscriptions', 'platformPayments', 'platformReceipts',
  'platformSupportTickets', 'platformSupportMessages', 'platformCrmLeads',
  'platformCrmActivities', 'companies', 'tenantPublicProfiles',
]);

const ALL_TENANT_COLLECTIONS = Array.from(new Set([
  ...CORE_COLLECTIONS,
  ...FINANCE_COLLECTIONS,
  ...Object.values(COLLECTIONS || {}),
])).filter((name) => name && name !== 'tenants' && !EXCLUDED_COLLECTIONS.has(name));

const hasFinancePermission = (permissions = []) => permissions.some((permission) => {
  const value = String(permission || '').toLowerCase();
  return value === 'all' || value.includes('finance') || value.includes('billing') || value.includes('invoice') || value.includes('payment');
});

const rowsFromSnapshot = (snap) => (snap?.docs || []).map((row) => ({
  path: row.ref.path,
  data: row.data(),
}));

const safeTenantQuery = async (name, tenantId, max = 2500) => {
  const documents = new Map();
  const tenantRef = doc(db, 'tenants', tenantId);
  const attempts = [
    query(collection(db, name), where('tenantIdValue', '==', tenantId), limit(max)),
    query(collection(db, name), where('companyIdValue', '==', tenantId), limit(max)),
    query(collection(db, name), where('tenantId', '==', tenantId), limit(max)),
    query(collection(db, name), where('companyId', '==', tenantId), limit(max)),
    query(collection(db, name), where('tenantId', '==', tenantRef), limit(max)),
    query(collection(db, name), where('companyId', '==', tenantRef), limit(max)),
  ];

  for (const attempt of attempts) {
    try {
      const snap = await getDocs(attempt);
      rowsFromSnapshot(snap).forEach((row) => documents.set(row.path, row));
      if (snap.size) break;
    } catch (_) {}
  }

  // Newer modules often use tenants/{tenantId}/{collection}. Import that shape too.
  try {
    const nested = await getDocs(query(collection(db, 'tenants', tenantId, name), limit(max)));
    rowsFromSnapshot(nested).forEach((row) => documents.set(row.path, row));
  } catch (_) {}

  return { name, count: documents.size, documents: Array.from(documents.values()) };
};

const offlinePrefetchService = {
  async prefetch({ tenantId, permissions = [], includeFinance = null, onProgress, includeAllTenantCollections = true } = {}) {
    if (!tenantId) return { totalCollections: 0, totalDocuments: 0, localImported: 0, results: [] };

    const financeAllowed = includeFinance ?? hasFinancePermission(permissions);
    const names = includeAllTenantCollections
      ? ALL_TENANT_COLLECTIONS.filter((name) => financeAllowed || !FINANCE_COLLECTIONS.includes(name))
      : [...CORE_COLLECTIONS, ...(financeAllowed ? FINANCE_COLLECTIONS : [])];

    // Register even currently-empty tenant collections so the LAN server can
    // discover records later created in Firebase by an authorized remote user.
    if (serverModeService.isLocalConfigured()) {
      await serverModeService.configureSyncCollections(names).catch(() => null);
    }

    let localImported = 0;
    let localSkipped = 0;

    // Tenant settings/branding/workflow are required by almost every page.
    try {
      const tenantSnap = await getDoc(doc(db, 'tenants', tenantId));
      if (tenantSnap.exists() && serverModeService.isLocalConfigured()) {
        const mirrored = await serverModeService.importDocuments([{ path: tenantSnap.ref.path, data: tenantSnap.data() }], { tenantId }).catch(() => null);
        localImported += Number(mirrored?.imported || 0);
        localSkipped += Number(mirrored?.skipped || 0);
      }
    } catch (_) {}

    const results = [];
    for (let index = 0; index < names.length; index += 1) {
      const name = names[index];
      const result = await safeTenantQuery(name, tenantId).catch(() => ({ name, count: 0, documents: [] }));
      results.push({ name: result.name, count: result.count });
      if (result.documents?.length && serverModeService.isLocalConfigured()) {
        const mirrored = await serverModeService.importDocuments(result.documents, { tenantId }).catch(() => null);
        localImported += Number(mirrored?.imported || 0);
        localSkipped += Number(mirrored?.skipped || 0);
      }
      if (typeof onProgress === 'function') {
        onProgress({ index: index + 1, total: names.length, name, count: result.count, localImported, localSkipped });
      }
    }

    return {
      totalCollections: results.length,
      totalDocuments: results.reduce((sum, row) => sum + Number(row.count || 0), 0),
      localImported,
      localSkipped,
      results,
      completedAt: new Date().toISOString(),
    };
  },
};

export default offlinePrefetchService;
